import React, { useState } from "react";
import {
  TextField,
  Button,
  Snackbar,
  Alert,
  AlertColor,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
} from "@mui/material";
import axios from "axios";
import { Typography } from "antd";
import { authenticationService } from "src/services";
import { multy_post } from 'src/services/httpClient.service';

const AddYoutube = (props) => {
  const APIURL = process.env.REACT_APP_BASE_API_URL
  const { vendorId, franchiseId } = authenticationService.getVendorFranchiseIds();

  const [formData, setFormData] = useState({
    thumbnailImage: null, // File object
    title: "",
    categoryName: "",
    videoUrl: "",
    image: "",
    priority: "",
    vendorId: vendorId,
  });
  
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: AlertColor;
  }>({
    open: false,
    message: "",
    severity: "success",
  });
      
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    setFormData({
      ...formData,
      thumbnailImage: file,
    });
  };

const handleAdd = async () => {
  if (!formData.thumbnailImage || !formData.title || !formData.videoUrl || !formData.categoryName || !formData.priority) {
    setSnackbar({ 
      open: true, 
      message: "Please fill in all required fields and upload an image.", 
      severity: "warning" 
    });
    return;
  }

  const formDataToSend = new FormData();
  
  // Create the appData object with all the form fields
  const appData = {
    title: formData.title,
    categoryName: formData.categoryName,
    videoUrl: formData.videoUrl,
    image: formData.image || "",
    priority: formData.priority,
    vendorId: vendorId
  };
  
  // Append appData as a JSON string with the part name "appData"
  formDataToSend.append("appData", new Blob([JSON.stringify(appData)], { type: "application/json" }));
  
  // Append the thumbnail file
  formDataToSend.append("thumbnailFile", formData.thumbnailImage);

  try {
    setLoading(true);
    const response = await multy_post(
      `${APIURL}/api/youtube-apps`,
      formDataToSend
    );
    
    props.fetchYoutubeData();
    setSnackbar({ open: true, message: "YouTube App Added Successfully!", severity: "success" });
    props.handleClose();
    
    // Reset form
    setFormData({ 
      thumbnailImage: null, 
      title: "", 
      categoryName: "",
      videoUrl: "", 
      image: "",
      priority: "",
      vendorId: vendorId,
    });
  } catch (error) {
    console.error("Error adding YouTube app:", error);
    setSnackbar({ open: true, message: "Failed to add YouTube app. Please try again.", severity: "error" });
  } finally {
    setLoading(false);
  }
};

  const handleSnackbarClose = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  return (
    <>
      <Grid container spacing={2} sx={{ p: 2 }}>
        {/* Thumbnail Image Upload */}
        <Grid item xs={12}>
          <Button variant="contained" component="label">
            Thumbnail Image *
            <input
              type="file"
              accept="image/jpeg, image/jpg, image/png"
              hidden
              onChange={handleFileChange}
            />
          </Button>
          {formData.thumbnailImage && (
            <div style={{ marginTop: 10 }}>
              <p>Preview:</p>
              <img
                src={URL.createObjectURL(formData.thumbnailImage)}
                alt="Thumbnail Preview"
                style={{
                  width: 100,
                  height: 100,
                  borderRadius: 5,
                  border: "1px solid #ccc",
                }}
              />
            </div>
          )}
        </Grid>

        {/* Title Field */}
        <Grid item xs={12}>
          <TextField
            margin="dense"
            label="Title *"
            name="title"
            value={formData.title}
            onChange={handleInputChange}
            fullWidth
            variant="outlined"
            required
          />
        </Grid>

        {/* Category Name Field */}
        <Grid item xs={12}>
          <TextField
            margin="dense"
            label="Category Name *"
            name="categoryName"
            value={formData.categoryName}
            onChange={handleInputChange}
            fullWidth
            variant="outlined"
            required
          />
        </Grid>

        {/* Video URL Field */}
        <Grid item xs={12}>
          <TextField
            margin="dense"
            label="Video URL *"
            name="videoUrl"
            value={formData.videoUrl}
            onChange={handleInputChange}
            fullWidth
            variant="outlined"
            required
          />
        </Grid>

        {/* Image URL Field */}
        <Grid item xs={12}>
          <TextField
            margin="dense"
            label="Image URL"
            name="image"
            value={formData.image}
            onChange={handleInputChange}
            fullWidth
            variant="outlined"
            helperText="Optional: External image URL"
          />
        </Grid>

        {/* Priority Field */}
        <Grid item xs={12}>
          <FormControl fullWidth margin="dense">
            <InputLabel id="priority-label">Priority *</InputLabel>
            <Select
              labelId="priority-label"
              name="priority"
              value={formData.priority}
              onChange={handleInputChange}
              label="Priority *"
              required
            >
              <MenuItem value="HIGH">High</MenuItem>
              <MenuItem value="MEDIUM">Medium</MenuItem>
              <MenuItem value="LOW">Low</MenuItem>
            </Select>
          </FormControl>
        </Grid>

        {/* Vendor ID (Hidden/Display Only) */}
        <Grid item xs={12}>
          <TextField
            margin="dense"
            label="Vendor ID"
            name="vendorId"
            value={formData.vendorId}
            InputProps={{
              readOnly: true,
            }}
            fullWidth
            variant="outlined"
            disabled
          />
        </Grid>
      </Grid>

      {/* Action Buttons */}
      <Box sx={{ margin: "20px", display: 'flex', flexDirection: 'row-reverse', gap: 1 }}>
        <Button onClick={props.handleClose} color="primary" disabled={loading}>
          Cancel
        </Button>
        <Button onClick={handleAdd} color="primary" disabled={loading} variant="contained">
          {loading ? "Adding..." : "Add"}
        </Button>
      </Box>

      {/* Snackbar for notifications */}
      <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={handleSnackbarClose}>
        <Alert onClose={handleSnackbarClose} severity={snackbar.severity} sx={{ width: "100%" }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default AddYoutube;